import React, { useState, useEffect } from 'react';
import { apiService, Action } from '../services/api';
import logger from '../utils/logger';

const ActionManager: React.FC = () => {
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Debug environment variables
  React.useEffect(() => {
    logger.info('ActionManager Environment Debug:', {
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
      REACT_APP_CLIENT_API_KEY: process.env.REACT_APP_CLIENT_API_KEY ? 'Present' : 'Missing',
      NODE_ENV: process.env.NODE_ENV
    });
  }, []);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newAction, setNewAction] = useState({ actionName: '', description: '' });
  const [editAction, setEditAction] = useState({ actionName: '', description: '' });

  useEffect(() => {
    loadActions();
  }, []);

  const loadActions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getActions();
      setActions(data);
    } catch (err) {
      setError('Failed to load actions');
      logger.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAction.actionName.trim()) return;

    try {
      await apiService.createAction({
        actionName: newAction.actionName.trim(),
        description: newAction.description.trim()
      });
      setNewAction({ actionName: '', description: '' });
      await loadActions();
    } catch (err: any) {
      logger.error('Create error:', err);
      if (err.response?.status === 409) {
        setError(`Action name '${newAction.actionName.trim()}' already exists. Please choose a different name.`);
      } else {
        setError('Failed to create action');
      }
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editAction.actionName.trim()) return;

    try {
      await apiService.updateAction(id, {
        actionName: editAction.actionName.trim(),
        description: editAction.description.trim()
      });
      setEditingId(null);
      await loadActions();
    } catch (err: any) {
      logger.error('Update error:', err);
      if (err.response?.status === 409) {
        setError(`Action name '${editAction.actionName.trim()}' already exists. Please choose a different name.`);
      } else {
        setError('Failed to update action');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this action?')) return;

    try {
      await apiService.deleteAction(id);
      await loadActions();
    } catch (err) {
      setError('Failed to delete action');
      logger.error('Delete error:', err);
    }
  };

  const startEdit = (action: Action) => {
    setEditingId(action.id!);
    setEditAction({
      actionName: action.actionName,
      description: action.description || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditAction({ actionName: '', description: '' });
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null); // Clear any previous errors
      const result = await apiService.uploadActionsCSV(file);
      
      // Create detailed success message
      let message = `Successfully processed ${result.totalProcessed} actions:\n`;
      message += `- ${result.insertedCount} new actions created\n`;
      
      if (result.duplicates.length > 0) {
        message += `- ${result.duplicates.length} duplicates skipped: ${result.duplicates.join(', ')}\n`;
      }
      
      if (result.errors.length > 0) {
        message += `- ${result.errors.length} errors occurred:\n${result.errors.join('\n')}`;
      }
      
      alert(message);
      await loadActions();
    } catch (err: any) {
      logger.error('Upload error:', err);
      
      // Try to extract meaningful error message from the response
      let errorMessage = 'Failed to upload CSV';
      
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your API key configuration.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access forbidden. Insufficient permissions.';
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
          if (err.response.data.details && Array.isArray(err.response.data.details)) {
            errorMessage += '\nDetails:\n' + err.response.data.details.join('\n');
          }
        } else {
          errorMessage = `Server error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Network error - request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      e.target.value = '';
    }
  };

  const handleCSVDownload = async () => {
    try {
      const blob = await apiService.downloadActionsCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'actions.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download CSV');
      logger.error('Download error:', err);
    }
  };


  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading actions...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Add New Action */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Action</h2>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action Name *
              </label>
              <input
                type="text"
                value={newAction.actionName}
                onChange={(e) => setNewAction({ ...newAction, actionName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Play Music, Order Food"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={newAction.description}
              onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe what this action does..."
              rows={3}
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Add Action
          </button>
        </form>
      </div>

      {/* CSV Operations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Bulk Operations</h3>
        <div className="flex flex-wrap gap-4">
          <div>
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 cursor-pointer"
            >
              Upload CSV
            </label>
          </div>
          <button
            onClick={handleCSVDownload}
            className="px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Download CSV
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          CSV format: actionName,description (header row required). Description is optional. Use quotes for values containing commas.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3 w-full">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700 whitespace-pre-line">{error}</div>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm text-red-600 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Actions ({actions.length})
        </h3>

        {actions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No actions found. Add some actions to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {actions.map((action) => (
                  <tr key={action.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      #{action.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingId === action.id ? (
                        <input
                          type="text"
                          value={editAction.actionName}
                          onChange={(e) => setEditAction({ ...editAction, actionName: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900">
                          {action.actionName}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingId === action.id ? (
                        <textarea
                          value={editAction.description}
                          onChange={(e) => setEditAction({ ...editAction, description: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          rows={2}
                        />
                      ) : (
                        <span className="text-sm text-gray-900">
                          {action.description || '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {action.created_at ? new Date(action.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {editingId === action.id ? (
                        <>
                          <button
                            onClick={() => handleUpdate(action.id!)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(action)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(action.id!)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionManager;